import type { DataLayerObject } from '@gtm-support/core'

import type { CatalogBundleStorefrontDtoWithContent } from '@/types/catalogWithContent'

import type {
  CartBundleStorefrontDto,
  CatalogBundleStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

interface GA4Item {
  item_id: string
  item_name: string
  item_category?: string
  price: number
  quantity: number
  discount?: number
}

class AppAnalyticsService {
  private static instance: AppAnalyticsService | null = null

  private constructor() {}

  static getInstance(): AppAnalyticsService {
    if (!AppAnalyticsService.instance) {
      AppAnalyticsService.instance = new AppAnalyticsService()
    }
    return AppAnalyticsService.instance
  }

  trackViewItemList(bundles: CatalogBundleStorefrontDtoWithContent[]): void {
    this.push('view_item_list', {
      ecommerce: {
        currency: 'USD',
        items: bundles.map((b) => this.buildCatalogItem(b, b.sessionTypeKey))
      }
    })
  }

  trackViewItem(bundle: CatalogBundleStorefrontDtoWithContent): void {
    this.push('view_item', {
      ecommerce: {
        currency: 'USD',
        value: bundle.pricing?.basePrice ?? 0,
        items: [this.buildCatalogItem(bundle, bundle.sessionTypeKey)]
      }
    })
  }

  trackAddToCart(bundle: CatalogBundleStorefrontDtoWithContent, value: number): void {
    this.push('add_to_cart', {
      ecommerce: {
        currency: 'USD',
        value,
        items: [this.buildCatalogItem(bundle, bundle.sessionTypeKey)]
      }
    })
  }

  trackRemoveFromCart(cartBundle: CartBundleStorefrontDto): void {
    this.push('remove_from_cart', {
      ecommerce: {
        currency: 'USD',
        value: cartBundle.unitCost * cartBundle.quantity,
        items: [this.buildCartItem(cartBundle)]
      }
    })
  }

  trackViewCart(cartBundles: CartBundleStorefrontDto[], value: number): void {
    this.push('view_cart', {
      ecommerce: {
        currency: 'USD',
        value,
        items: cartBundles.map((b) => this.buildCartItem(b))
      }
    })
  }

  trackBeginCheckout(cartBundles: CartBundleStorefrontDto[], value: number): void {
    this.push('begin_checkout', {
      ecommerce: {
        currency: 'USD',
        value,
        items: cartBundles.map((b) => this.buildCartItem(b))
      }
    })
  }

  trackAddShippingInfo(cartBundles: CartBundleStorefrontDto[]): void {
    this.push('add_shipping_info', {
      ecommerce: {
        currency: 'USD',
        items: cartBundles.map((b) => this.buildCartItem(b))
      }
    })
  }

  trackPurchase(orderId: string, cartBundles: CartBundleStorefrontDto[], value: number): void {
    this.push('purchase', {
      ecommerce: {
        transaction_id: orderId,
        currency: 'USD',
        value,
        items: cartBundles.map((b) => this.buildCartItem(b))
      }
    })
  }

  trackRetouchingSelected(): void {
    this.push('retouching_selected')
  }

  trackUpsellShown(upsellName: string): void {
    this.push('upsell_shown', { upsell_name: upsellName })
  }

  trackUpsellAccepted(upsellName: string): void {
    this.push('upsell_accepted', { upsell_name: upsellName })
  }

  trackUpsellDeclined(upsellName: string): void {
    this.push('upsell_declined', { upsell_name: upsellName })
  }

  trackUpgradeShown(fromTier: string, toTier: string): void {
    this.push('upgrade_shown', { from_tier: fromTier, to_tier: toTier })
  }

  trackUpgradeAccepted(fromTier: string, toTier: string): void {
    this.push('upgrade_accepted', { from_tier: fromTier, to_tier: toTier })
  }

  trackUpgradeDeclined(fromTier: string, toTier: string): void {
    this.push('upgrade_declined', { from_tier: fromTier, to_tier: toTier })
  }

  trackSceneInspirationModalShown(): void {
    this.push('scene_inspiration_modal_shown')
  }

  trackSceneInspirationModalDismissed(sceneIds: string[], sceneNames: string[]): void {
    this.push('scene_inspiration_modal_dismissed', { scene_ids: sceneIds, scene_names: sceneNames })
  }

  trackSceneInspirationModalSceneSelected(sceneId: string, sceneName: string): void {
    this.push('scene_inspiration_modal_scene_selected', {
      scene_id: sceneId,
      scene_name: sceneName
    })
  }

  trackSceneInspirationModalSceneDeselected(sceneId: string, sceneName: string): void {
    this.push('scene_inspiration_modal_scene_deselected', {
      scene_id: sceneId,
      scene_name: sceneName
    })
  }

  trackSceneInspirationModalSeeSessionsClicked(sceneIds: string[], sceneNames: string[]): void {
    this.push('scene_inspiration_modal_see_sessions_clicked', {
      scene_ids: sceneIds,
      scene_names: sceneNames
    })
  }

  private buildCatalogItem(bundle: CatalogBundleStorefrontDto, sessionTypeKey?: string): GA4Item {
    return {
      item_id: bundle.catalogBundleId,
      item_name: bundle.bundle?.nameInternal ?? bundle.catalogBundleId,
      item_category: sessionTypeKey,
      price: bundle.pricing?.basePrice ?? 0,
      quantity: 1
    }
  }

  private buildCartItem(cartBundle: CartBundleStorefrontDto): GA4Item {
    const item: GA4Item = {
      item_id: cartBundle.catalogBundleId,
      item_name: cartBundle.bundleNameInternal,
      price: cartBundle.unitCost,
      quantity: cartBundle.quantity
    }
    if (cartBundle.bundleDiscount > 0) {
      item.discount = cartBundle.bundleDiscount
    }
    return item
  }

  private push(eventName: string, payload: Record<string, unknown> = {}): void {
    if (import.meta.env.DEV) {
      console.debug('[Analytics]', eventName, payload)
    }
    window.dataLayer = window.dataLayer ?? []
    ;(window.dataLayer as DataLayerObject[]).push({ ecommerce: null })
    ;(window.dataLayer as DataLayerObject[]).push({
      event: 'eCommerce - All Events',
      event_name: eventName,
      ...payload
    })
  }
}

export const appAnalyticsService = AppAnalyticsService.getInstance()
